import { QuizBase } from '../../redux/quiz/types'

// e7 ("Symptom score v2") scoring — kept separate from the e5/e6 `utils.ts` because
// the scale is REVERSED: here slider answers are stored '1'-'4' where
// 1 = Not at all (best) and 4 = Extremely (worst), so a HIGHER score means MORE
// severe symptoms. e5/e6 store the inverse, so they must not share this logic.

// 15 base symptom questions, asked of every user.
export const E7_BASE_SYMPTOM_KEYS = [
  // Gut and digestion
  'digestive_discomfort',
  'bloating',
  'acid_reflux_and_indigestion',
  'irregular_bowel_movements',
  // Inflammation
  'joint_and_muscular_discomfort',
  'decreased_physical_strength_or_stamina',
  'skin_issues',
  'hair_loss',
  // Energy and mood
  'tiredness_and_fatigue',
  'brain_fog_or_trouble_concentrating',
  'poor_sleep_quality',
  'stress_or_low_mood',
  // Appetite and weight
  'hunger',
  'cravings',
  'weight_gain_or_difficulty_managing_weight',
] as const

// 4 hormonal questions, only asked of women aged 35+ (see e7.config showWhen).
export const E7_HORMONAL_SYMPTOM_KEYS = [
  'night_sweats',
  'hot_flushes',
  'irritability',
  'loss_of_interest_in_sex',
] as const

export type E7SymptomKey = (typeof E7_BASE_SYMPTOM_KEYS)[number] | (typeof E7_HORMONAL_SYMPTOM_KEYS)[number]

// Verbatim question-screen labels — reused on the results screen so the wording matches.
export const E7_SYMPTOM_LABELS: Record<E7SymptomKey, string> = {
  digestive_discomfort: 'Digestive discomfort',
  bloating: 'Bloating',
  acid_reflux_and_indigestion: 'Acid reflux and indigestion',
  irregular_bowel_movements: 'Irregular bowel movements',
  joint_and_muscular_discomfort: 'Joint and muscular discomfort',
  decreased_physical_strength_or_stamina: 'Decreased physical strength or stamina',
  skin_issues: 'Skin issues',
  hair_loss: 'Hair loss',
  tiredness_and_fatigue: 'Tiredness and fatigue',
  brain_fog_or_trouble_concentrating: 'Brain fog or trouble concentrating',
  poor_sleep_quality: 'Poor sleep quality',
  stress_or_low_mood: 'Stress or low mood',
  hunger: 'Hunger',
  cravings: 'Cravings',
  weight_gain_or_difficulty_managing_weight: 'Weight gain or difficulty managing weight',
  night_sweats: 'Night sweats',
  hot_flushes: 'Hot flushes',
  irritability: 'Irritability',
  loss_of_interest_in_sex: 'Loss of interest in sex',
}

// Per-symptom descriptions shown on the results-screen key-symptom cards
// (verbatim from the Figma "KEY SYMPTOM COPY" annotations for 7a/7b).
export const E7_SYMPTOM_DESCRIPTIONS: Record<E7SymptomKey, string> = {
  tiredness_and_fatigue:
    'Gut health affects nutrient absorption and inflammation. When things are out of balance, you may feel more tired or fatigued.',
  poor_sleep_quality:
    'An imbalanced gut microbiome can effect the hormones and signals your body needs to rest properly.',
  weight_gain_or_difficulty_managing_weight:
    'A disrupted gut microbiome can affect appetite signals and how efficiently your body uses energy, making it harder to manage weight.',
  hunger:
    'Your gut microbiome helps regulate appetite through hormones. When your gut is out of balance, hunger signals can be harder to manage.',
  stress_or_low_mood:
    'Your gut and brain are strongly connected. When your gut microbiome is out of balance, it can negatively impact your mood and levels of stress.',
  digestive_discomfort:
    'A disrupted gut microbiome can irritate the gut lining and negatively impact how nutrients are broken down, which can contribute to discomfort after meals.',
  cravings:
    'Cravings (especially for sugary or ultra processed foods) can feel stronger when your gut microbiome is out of balance, due to disrupted reward signals.',
  bloating:
    'Your gut bacteria help break down nutrients. When the microbiome is disrupted, this process can produce more gas which can lead to bloating.',
  decreased_physical_strength_or_stamina:
    'Your muscles need nutrients to perform, and a healthy gut microbiome helps you absorb these nutrients.',
  brain_fog_or_trouble_concentrating:
    'Inflammation and disrupted communication between your gut and brain can make it harder to think clearly. Both can be caused by an imbalanced gut microbiome.',
  irregular_bowel_movements:
    'There are trillions of bacteria in your gut which play a key role in your digestion. An imbalanced microbiome can disrupt this, making irregular bowel movements more likely.',
  acid_reflux_and_indigestion:
    'When the gut microbiome is out of balance, digestion can be less efficient, which may contribute to indigestion and reflux symptoms.',
  joint_and_muscular_discomfort:
    'Chronic joint and muscular discomfort is often linked to low-grade inflammation, which can be influenced by your gut health.',
  skin_issues:
    'A disrupted gut microbiome can increase inflammation in the body, which may show up on the skin as irritation or breakouts.',
  hair_loss: 'Healthy hair growth requires key nutrients that are absorbed by a healthy gut microbiome.',
  irritability:
    'A disrupted gut microbiome can affect mood-related signals in the brain and leave you feeling more irritable.',
  night_sweats:
    'An imbalanced gut microbiome can affect how your body regulates temperature, making night sweats more frequent or intense.',
  hot_flushes:
    'Gut health plays a role in how your body manages hormonal fluctuations. An imbalanced microbiome can make hot flushes more frequent or severe.',
  loss_of_interest_in_sex:
    'Your gut microbiome influences hormone regulation and mood. When it’s out of balance, it can contribute to lower libido and reduced interest in sex.',
}

// Tiebreak priority when two symptoms share the same answer value (index 0 =
// highest priority). Two orderings: one for the 15-question flow and one for the
// 19-question flow when hormonal symptoms were asked.
const TOP_SYMPTOM_PRIORITY_BASE: E7SymptomKey[] = [
  'tiredness_and_fatigue',
  'poor_sleep_quality',
  'weight_gain_or_difficulty_managing_weight',
  'hunger',
  'stress_or_low_mood',
  'digestive_discomfort',
  'cravings',
  'bloating',
  'decreased_physical_strength_or_stamina',
  'brain_fog_or_trouble_concentrating',
  'irregular_bowel_movements',
  'acid_reflux_and_indigestion',
  'joint_and_muscular_discomfort',
  'skin_issues',
  'hair_loss',
]

const TOP_SYMPTOM_PRIORITY_WITH_HORMONAL: E7SymptomKey[] = [
  'tiredness_and_fatigue',
  'poor_sleep_quality',
  'weight_gain_or_difficulty_managing_weight',
  'hunger',
  'stress_or_low_mood',
  'digestive_discomfort',
  'cravings',
  'bloating',
  'irritability',
  'hot_flushes',
  'decreased_physical_strength_or_stamina',
  'brain_fog_or_trouble_concentrating',
  'irregular_bowel_movements',
  'acid_reflux_and_indigestion',
  'joint_and_muscular_discomfort',
  'night_sweats',
  'skin_issues',
  'hair_loss',
  'loss_of_interest_in_sex',
]

const parseAnswer = (raw: string | undefined): number | null => {
  if (!raw) return null
  const n = parseInt(raw, 10)
  return isNaN(n) ? null : n
}

// Hormonal questions are only included once the user has actually answered them.
export const hasHormonalSymptoms = (quiz: QuizBase): boolean =>
  E7_HORMONAL_SYMPTOM_KEYS.some((k) => parseAnswer(quiz[k]) !== null)

const getActiveSymptomKeys = (quiz: QuizBase): E7SymptomKey[] =>
  hasHormonalSymptoms(quiz) ? [...E7_BASE_SYMPTOM_KEYS, ...E7_HORMONAL_SYMPTOM_KEYS] : [...E7_BASE_SYMPTOM_KEYS]

// finalScore = ((rawScore - min) / (max - min)) * 100, where min = count of active
// questions (all answered '1') and max = count * 4. Higher = more severe.
export const calculateE7SymptomScore = (quiz: QuizBase): number => {
  const keys = getActiveSymptomKeys(quiz)
  const count = keys.length
  // Backfill any missing answer with 1 (Not at all / best) so a partial state can't inflate the score.
  const raw = keys.reduce((acc, k) => acc + (parseAnswer(quiz[k]) ?? 1), 0)
  const min = count
  const max = count * 4
  const normalised = ((raw - min) / (max - min)) * 100
  return Math.round(Math.min(100, Math.max(0, normalised)))
}

export type E7ScoreCategory = '0-20' | '21-49' | '50-100'

export const getE7ScoreCategory = (score: number): E7ScoreCategory => {
  if (score <= 20) return '0-20'
  if (score <= 49) return '21-49'
  return '50-100'
}

export type E7Severity = 'Severe' | 'Significant' | 'Moderate'

// Quite a bit / Extremely → Severe, A little → Significant, Not at all → Moderate.
export const getE7Severity = (rawAnswer: string | undefined): E7Severity => {
  const n = parseAnswer(rawAnswer)
  if (n !== null && n >= 3) return 'Severe'
  if (n === 2) return 'Significant'
  return 'Moderate'
}

export type E7TopSymptom = {
  key: E7SymptomKey
  label: string
  description?: string
  severity: E7Severity
}

// Always returns exactly 3: descending by answer value (worst first), tiebroken by
// the documented priority order for the active question set.
export const getE7TopSymptoms = (quiz: QuizBase): E7TopSymptom[] => {
  const keys = getActiveSymptomKeys(quiz)
  const priority = hasHormonalSymptoms(quiz) ? TOP_SYMPTOM_PRIORITY_WITH_HORMONAL : TOP_SYMPTOM_PRIORITY_BASE

  const scored = keys.map((key) => ({ key, value: parseAnswer(quiz[key]) ?? 1 }))
  scored.sort((a, b) => {
    if (a.value !== b.value) return b.value - a.value
    return priority.indexOf(a.key) - priority.indexOf(b.key)
  })

  return scored.slice(0, 3).map(({ key }) => ({
    key,
    label: E7_SYMPTOM_LABELS[key],
    description: E7_SYMPTOM_DESCRIPTIONS[key],
    severity: getE7Severity(quiz[key]),
  }))
}

// Future-you projection: a 34% reduction in the symptom score.
export const calculateE7FutureScore = (score: number): number => score - Math.round(score * 0.34)
